// Questions tab: useFieldArray of question cards and add-question button.

import React from "react";
import {
    Button,
    Card,
    CardBody,
    IconButton,
    TabPanel,
    Tooltip,
    Typography,
} from "@material-tailwind/react";
import { useFieldArray, useFormContext } from "react-hook-form";
import DeleteQuestionDialog from "components/dialog/delete-question-dialog";
import QuestionSettingsDialog from "components/dialog/question-settings-dialog";
import QuestionAnswerInputs from "components/form/question-answers";
import RHFInput from "components/form/rhf-input";
import MaterialIcon from "components/material-icon";
import { isEditable } from "../lifecycle-visibility";

// P4 child: one question card. Nested hooks (the preset-answer
// useFieldArray in QuestionAnswerInputs) live in children, never in a
// .map() callback (Decision 3; Wave 1 proof at
// tests/components/form/rhf-adapters.test.jsx:131).
function QuestionCard({
    index,
    displayIndex,
    totalQuestions,
    readOnly,
    swap,
    insert,
    handleSettingsOpen,
    handleCloneQuestion,
    setQuestionChop,
    moveQuestionUp,
    moveQuestionDown,
}) {
    const { getValues, setValue, watch } = useFormContext();

    // Live read: the settings dialog writes question_type with a leaf
    // setValue, which never refreshes the useFieldArray fields mirror the
    // card is rendered from. watch keeps the label and the answer-inputs
    // mount gate in step with the store.
    const questionType = watch(`questions.${index}.question_type`);

    // Adapter for the shared pure handlers in question-handlers.js, which
    // keep their (swap, values, index, setFieldValue) signature.
    const setFieldValue = (name, value) =>
        setValue(name, value, { shouldDirty: true, shouldValidate: true });

    return (
        <Card className="card mb-4 p-0">
            <CardBody className="card-inner-body">
                <div className="flex flex-col gap-2">
                    <div className="flex justify-between gap-2">
                        <div className="font-display mb-1 min-w-fit font-bold text-black p-2 ">
                            Q.
                            {displayIndex + 1}
                        </div>
                        <div className="text-black bg-white font-body px-4 py-2 rounded-2xl">
                            <Typography variant="h6">
                                Question type:
                                <span className="inline ml-2">
                                    {questionType === "mixed"
                                        ? "Mixed"
                                        : questionType === "freeform"
                                            ? "Gobby®"
                                            : "Preset Answers"}
                                </span>
                            </Typography>
                        </div>
                        <div>
                            <Tooltip
                                className="font-body"
                                content="Question settings."
                                animate={{
                                    mount: {
                                        scale: 1,
                                        y: 0,
                                    },
                                    unmount: {
                                        scale: 0,
                                        y: 25,
                                    },
                                }}
                            >
                                <IconButton
                                    className="btn btn--cta px-8"
                                    type="button"
                                    variant="text"
                                    onClick={() =>
                                        handleSettingsOpen(index)
                                    }
                                >
                                    <MaterialIcon
                                        name="settings"
                                        size="2xl"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>
                    <div className=" p-2 mt-2 font-body sm:rounded-2xl rounded-xl">
                        <RHFInput
                            name={`questions.${index}.text`}
                            type="text"
                            color="black"
                            label="Please enter your question."
                            disabled={readOnly}
                        />
                    </div>
                    {(questionType === "fixed" ||
                        questionType === "mixed") && (
                        <QuestionAnswerInputs
                            questionIndex={index}
                            isReadOnly={readOnly}
                        />
                    )}
                </div>
                <div className="flex flex-row justify-end">
                    <div className="rounded-2xl bg-white w-fit p-2 flex flex-row gap-2">
                        <Tooltip
                            className="font-body"
                            content="Move this question up one position"
                            animate={{
                                mount: {
                                    scale: 1,
                                    y: 0,
                                },
                                unmount: {
                                    scale: 0,
                                    y: 25,
                                },
                            }}
                        >
                            <IconButton
                                className="btn-icon--secondary"
                                type="button"
                                variant="text"
                                disabled={
                                    index === 0 ||
                                    readOnly
                                }
                                onClick={() =>
                                    moveQuestionUp(
                                        swap,
                                        getValues(),
                                        index,
                                        setFieldValue,
                                    )
                                }
                            >
                                <MaterialIcon
                                    name="arrow_upward"
                                    size="xl"
                                />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            className="font-body"
                            content="Move this question down one position"
                            animate={{
                                mount: {
                                    scale: 1,
                                    y: 0,
                                },
                                unmount: {
                                    scale: 0,
                                    y: 25,
                                },
                            }}
                        >
                            <IconButton
                                className="btn-icon--secondary"
                                type="button"
                                variant="text"
                                disabled={
                                    index ===
                                        totalQuestions - 1 ||
                                    readOnly
                                }
                                onClick={() =>
                                    moveQuestionDown(
                                        swap,
                                        getValues(),
                                        index,
                                        setFieldValue,
                                    )
                                }
                            >
                                <MaterialIcon
                                    name="arrow_downward"
                                    size="xl"
                                />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            content="Duplicate this question."
                            className="font-body"
                            animate={{
                                mount: {
                                    scale: 1,
                                    y: 0,
                                },
                                unmount: {
                                    scale: 0,
                                    y: 25,
                                },
                            }}
                        >
                            <IconButton
                                className="btn-icon--info"
                                type="button"
                                variant="text"
                                onClick={() =>
                                    handleCloneQuestion(
                                        index,
                                        // Live read: the fields mirror
                                        // does not carry in-card edits, so
                                        // the clone payload must come from
                                        // the store or the duplicate
                                        // persists pre-edit text.
                                        getValues(`questions.${index}`),
                                        insert,
                                    )
                                }
                                disabled={readOnly}
                            >
                                <MaterialIcon
                                    name="content_copy"
                                    size="xl"
                                />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            className="font-body"
                            content="Delete this question."
                            animate={{
                                mount: {
                                    scale: 1,
                                    y: 0,
                                },
                                unmount: {
                                    scale: 0,
                                    y: 25,
                                },
                            }}
                        >
                            <IconButton
                                className="btn-icon--danger"
                                type="button"
                                variant="text"
                                onClick={() =>
                                    setQuestionChop(index)
                                }
                                disabled={readOnly}
                            >
                                <MaterialIcon
                                    name="delete"
                                    size="xl"
                                />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
}

export default function QuestionsPanel({
    survey,
    onAutoSave,
    handleSettingsOpen,
    handleCloneQuestion,
    handleAddQuestion,
    setQuestionChop,
    moveQuestionUp,
    moveQuestionDown,
    showQuestionDeleteDialog,
    setShowQuestionDeleteDialog,
    questionToDelete,
    setQuestionToDelete,
    setQuestionsToDelete,
    showSettingsDialog,
    setShowSettingsDialog,
    questionEditIndex,
}) {
    const { control, getValues } = useFormContext();
    const {
        fields: questionFields,
        append,
        insert,
        remove,
        swap,
    } = useFieldArray({ name: "questions", control });
    const readOnly = !isEditable(survey.state);

    return (
        <TabPanel value="questions">
            {questionFields
                .map((field, index) => ({ field, index }))
                .sort((a, b) => {
                    return a.field.position - b.field.position;
                })
                .map(({ field, index }, displayIndex) => (
                    <QuestionCard
                        // Stable row keys (I2): saved rows carry their
                        // server nanoid across reseeds; unsaved rows fall
                        // back to the generated field-array id.
                        key={field.nanoid || field.id}
                        index={index}
                        displayIndex={displayIndex}
                        totalQuestions={questionFields.length}
                        readOnly={readOnly}
                        swap={swap}
                        insert={insert}
                        handleSettingsOpen={handleSettingsOpen}
                        handleCloneQuestion={handleCloneQuestion}
                        setQuestionChop={setQuestionChop}
                        moveQuestionUp={moveQuestionUp}
                        moveQuestionDown={moveQuestionDown}
                    />
                ))}
            <div className="flex justify-center">
                <Button
                    className="btn btn--primary"
                    type="button"
                    onClick={() => {
                        handleAddQuestion(getValues("questions"), append);
                        setTimeout(() => onAutoSave(), 0);
                    }}
                    variant="filled"
                    disabled={readOnly}
                >
                    Add Another Question
                </Button>
            </div>
            <DeleteQuestionDialog
                showDialog={showQuestionDeleteDialog}
                deleteIndex={questionToDelete}
                removeCallback={remove}
                setDeleteArrayState={setQuestionsToDelete}
                onClose={() =>
                    setQuestionToDelete(undefined)
                }
                handleClose={() =>
                    setShowQuestionDeleteDialog(
                        (showQuestionDeleteDialog) =>
                            !showQuestionDeleteDialog,
                    )
                }
            />
            <QuestionSettingsDialog
                showDialog={showSettingsDialog}
                qI={questionEditIndex}
                handleClose={() =>
                    setShowSettingsDialog(
                        (showSettingsDialog) =>
                            !showSettingsDialog,
                    )
                }
                isReadOnly={readOnly}
            />
        </TabPanel>
    );
}
